import {Time, Util} from 'yougile-util'
import {DataObjectId, Department} from 'yougile-model'
import {SandboxTree} from 'yougile-model'
import {User} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {Accounts} from '../../client/accounts'
import {UserAccess} from '../../extensions/user-access'
import {Item} from './item'

/**
 * @name=user variable
 * @code
 * Users.get('... email or id');
 * @description
 * Represents a user, can be get by id or name by Users.get function
 * (and also from Object navigator).
 * @descriptionRu
 * Переменная представляющая пользователя. Может быть получена по
 * id или email-у из функции Users.get
 * (также можно получить из Навигатора по объектам).
 * @example
 * var john = Users.find('john@example.com');
 * // “john” is a user variable now,
 * // so we can access all properties available
 * // (name, email, id, status, photo, …)
 * if (john.status === 'online') {
 *   Notifier.warn('John is online!');
 * } else {
 *   Notifier.warn('John is offline!');
 * }
 * // all these properties are readonly, but you can change
 * // the way they are shown in to others by using methods
 * // of the “Users” object
 */
export class UserObj {
  public id: DataObjectId
  public box: SandboxTree /* api:exclude */

  constructor(id: DataObjectId, box: SandboxTree) {
    this.id = id
    this.box = box
  }

  /**
   * @code
   * Users.get('... user email or id').name;
   * @description
   * Returns user’s displayed name.
   * @descriptionRu
   * Возвращает отображаемое имя пользователя.
   */
  public get name(): string {
    return Accounts.get(this.id).realName
  }

  /**
   * @code
   * Users.get('... user email or id').photo;
   * @description
   * Shortcut for Users.get('... user email or id').avatar.
   * @descriptionRu
   * Сокращение для Users.get('... user email or id').avatar.
   */
  public get photo(): string {
    return this.avatar
  }

  /**
   * @code
   * Users.get('... user email or id').avatar;
   * @description
   * Returns url of user’s account photo, if photo is not set, then returns an empty string.
   * @descriptionRu
   * Возвращает url аватары пользователя. Если аватара не установлена, возвращает пустую строку.
   */
  public get avatar(): string {
    return Accounts.get(this.id).avatar
  }

  /**
   * @code
   * Users.get('... user email or id').email;
   * @description
   * Returns user’s email.
   */
  public get email(): string {
    const user = this.box.get(this.id) as User
    return user.email
  }

  /**
   * @code
   * Users.get('... user email or id').status;
   * @description
   * Returns user’s status. Possible values are: 'online', 'offline', 'dnd'.
   * @descriptionRu
   * Возвращает статус пользователя. Возможные значения: 'online', 'offline',
   * 'dnd'.
   */
  public get status(): string {
    const acc = Accounts.get(this.id)
    if (acc.status === 'online' && acc.dndUntil && acc.dndUntil > Time.now()) {
      return 'dnd'
    } else {
      return acc.status
    }
  }

  /**
   * @code
   * Users.get('... user email or id').dndUntil;
   * @example
   * var u = Users.get('peter@example.com');
   * if (u.status === 'dnd') {
   *   var dndEnd = new Date(u.dndUntil);
   *   Notifier.warn('Peter is in Do-not-disturb mode until ' + dndEnd);
   * }
   * @description
   * If user status is 'dnd' then this method returns unix timestamp
   * of dnd regime ending.
   * @descriptionRu
   * Если статус юзера - 'dnd', тогда этот метод возвращяет unix timestamp
   * окончания статуса dnd.
   */
  public get dndUntil(): number {
    if (this.status === 'dnd') {
      return Accounts.get(this.id).dndUntil
    } else {
      return 0
    }
  }

  /**
   * @code
   * Users.get('... user email or id').lastActivity;
   * @description
   * Returns the last time user was seen online (if the user is online,
   * returns current timestamp).
   * @descriptionRu
   * Возвращает время, когда юзер был онлайн последний раз (если пользователь
   * сейчас онлайн, возвращает текущий timestamp).
   */
  public get lastActivity(): number {
    const acc = Accounts.get(this.id)
    return acc.status === 'online' ? Time.now() : acc.lastActivity
  }

  /**
   * @code
   * Users.get('... user email or id').isAdmin;
   * @description
   * Returns true if the user is admin. This value is readonly and can’t
   * be changed with API.
   * @descriptionRu
   * Возвращает true, если юзер - админ. Это значение только для чтения и
   * не может быть изменено через API.
   */
  public get isAdmin(): boolean {
    return this.box.users.get(this.id).isAdmin
  }

  /**
   * @code
   * Users.get('... user email or id').getRole(Items.get('... item id'));
   * @description
   * Return role in project, containing the specified item
   * (project, board, column, task). Role in project can be 'admin',
   * 'worker', 'observer' or role id for curtom roles.
   * @descriptionRu
   * Возвращает роль в проекте, который содержит указанный объект типа item.
   * Значение роли может быть 'admin' (управляющий), 'worker' (сотрудник),
   * 'observer' (наблюдатель) или строка с id роли для кастомных ролей.
   * @example
   * var user = Current.user;
   * var board = Items.get('...');
   *
   * // role can be one of the following:
   * // admin, worker, observer or roleId
   * var role = user.getRole(board);
   * console.log(role);
   */
  public getRole(item: Item): string {
    const proj = this.box.getProjectFor(item.id)
    return proj ? UserAccess.getRoleInProject(this.id, proj) : undefined
  }

  /**
   * @code
   * Users.get('... user email or id').department;
   * @description
   * Returns array of department, where user is a member.
   */
  public get department(): Pick<Department, 'id' | 'title'>[] {
    return DepartmentsClient.listForUser(this.box, this.id).map(d => ({
      id: d.id,
      title: d.title
    }))
  }

  /**
   * @code
   * Users.get('... id').getData();
   * @example
   * var u = Users.get('... user id');
   * var d = t.getData();
   * u.data = {
   *   clientId: '9023929875'
   *   contact: {
   *     phone: '+1234567',
   *     email: 'some@example.com'
   *   }
   * };
   * // this data is now saved on server and can be accessed
   * // from scripts for all users.
   * @description
   * Gets custom data attached to the user (see setData function).
   * @descriptionRu
   * Получает пользовательские данные, прикреплённые к пользователю
   * (см. функцию setData).
   */
  public getData(): unknown {
    const u = this.box.get(this.id) as User
    return Util.clone(u.apiData) || {}
  }

  /**
   * @code
   * Users.get('... id').setData({...});
   * @example
   * var u = Users.get('... id');
   * var d = {
   *   clientId: '9023929875'
   *   contact: {
   *     phone: '+1234567',
   *     email: 'some@example.com'
   *   }
   * };
   * u.setData(d);
   * // this data is now saved on server and can be accessed
   * // from scripts for all users.
   *
   * // modification existent data example
   * var data = u.getData();
   * data.orderNum = 2341;
   * u.setData(data);
   * @description
   * Custom data can be attached to any user object
   * (JSON). This data is automatically
   * synchronized with server and all users and if available
   * with getData function.
   * @descriptionRu
   * К любому пользователю в компании можно добавить пользовательские
   * данные (JSON). Они будут автоматически синхронизированы
   * с сервером и доступны через функцию getData.
   */
  public setData(json: unknown): void {
    const u = this.box.get(this.id) as User
    u.apiData = Util.clone(json) || {}
    u.commit()
  }
}
